/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  Switch,
  useTheme,
  alpha
} from '@mui/material';
import { useParams, useHistory } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import {
  getDispenserDetails,
  updateDispenserDetails,
  clearDispenserCards
} from 'src/redux/dispenserSlice';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import useAuth from 'src/context/AuthContext';
import {
  getFormattedDateTime12Hour,
  hasAdminLikeAccess
} from 'src/utils/getInitials';
import NewDispenser from './NewDispenser';
import DispenserLinkedCardsModel from './formModel/DispenserLinkedCardsModel';

// -------- Helpers --------
const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined) return '--';

  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';

  if (typeof value === 'string' && value.includes('T')) {
    const formattedDateTime = getFormattedDateTime12Hour(value);
    if (formattedDateTime) return formattedDateTime;
  }

  return value;
};

const getTankName = (dispenser) => dispenser?.tank_name
  || dispenser?.tank?.name
  || dispenser?.tank?.tank_name
  || '--';

function DispenserDetails() {
  const theme = useTheme();
  const { dispenserId } = useParams();
  const history = useHistory();
  const { getUser } = useAuth();
  const hasAdminAccess = hasAdminLikeAccess(getUser);

  const dispatch = useDispatch();
  const { loading, dispenserDetails } = useSelector(
    (state) => state.dispensers
  );

  const [openModal, setOpenModal] = useState(false);
  const [showLinkedCardsModal, setShowLinkedCardsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalInfo, setModalInfo] = useState(
    CONFIRMATION_MSG.disableDispenser
  );
  const [actionName, setActionName] = useState(null);

  const ACTION_TYPES = { update: 'UPDATE', reset: 'RESET' };

  useEffect(() => {
    dispatch(getDispenserDetails(dispenserId));
  }, [dispatch, dispenserId]);

  const openConfirmationModal = (type) => {
    if (type === ACTION_TYPES.reset) {
      setActionName(type);
      setModalInfo(CONFIRMATION_MSG.resetCards);
    } else {
      const info = dispenserDetails.status
        ? CONFIRMATION_MSG.disableDispenser
        : CONFIRMATION_MSG.enableDispenser;
      setActionName(null);
      setModalInfo(info);
    }
    setShowConfirmModal(true);
  };

  const updateDispenserStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    try {
      const values = {
        ...dispenserDetails,
        status: !dispenserDetails.status
      };
      await dispatch(updateDispenserDetails(values)).unwrap();
      dispatch(
        setSuccessInfo({
          successMsg: 'Dispenser status updated successfully!'
        })
      );
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    }
    setIsLoading(false);
  };

  const resetCards = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    try {
      await dispatch(clearDispenserCards(dispenserDetails.id)).unwrap();
      dispatch(
        setSuccessInfo({
          successMsg: 'Dispenser cards reset successfully!'
        })
      );
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    }
    setIsLoading(false);
  };

  const handleModalSubmit = () => {
    if (actionName === ACTION_TYPES.reset) resetCards();
    else updateDispenserStatus();
  };

  if (loading || isLoading) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

      <NewDispenser
        open={openModal}
        initialValues={dispenserDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 140px)',
        }}
      >

        {/* HEADER */}
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                DISPENSER DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage dispenser configuration
              </Typography>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.dispensers)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Back
            </Button>
          )}
        />

        {/* CONTENT */}
        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} key="station_name">
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 0.5
                  }}
                >
                  Dispenser Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {dispenserDetails.station_name || '--'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4} key="address">
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 0.5
                  }}
                >
                  Dispenser Address
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {dispenserDetails.address || '--'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4} key="site_name">
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 0.5
                  }}
                >
                  Site Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {dispenserDetails.site_name || '--'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4} key="tank_name">
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 0.5
                  }}
                >
                  Tank Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {getTankName(dispenserDetails)}
                </Typography>
              </Box>
            </Grid>

            {Object.entries(dispenserDetails || {}).map(([key, value]) => {
              if ([
                'station_name',
                'address',
                'site_name',
                'tank_name'
              ].includes(key)) {
                return null;
              }

              if (key === 'status') {
                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.02)
                        }
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          display="block"
                        >
                          STATUS
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={
                            value ? 'success.main' : 'error.main'
                          }
                        >
                          {value ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>

                      <Switch
                        checked={value}
                        onChange={() => openConfirmationModal(ACTION_TYPES.update)}
                        disabled={!hasAdminAccess}
                        color="success"
                      />
                    </Box>
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      height: '100%',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                      }
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5
                      }}
                    >
                      {formatLabel(key)}
                    </Typography>

                    <Typography variant="body1" fontWeight={600}>
                      {formatValue(value)}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>

        {/* ACTIONS */}
        {hasAdminAccess && (
          <>
            <Divider />
            <CardActions sx={{
              p: 2.5, justifyContent: 'flex-end', gap: 2, position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
            }}
            >
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Edit Dispenser
              </Button>

              <Button
                variant="contained"
                onClick={() => setShowLinkedCardsModal(true)}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                View Linked Cards
              </Button>

              {!dispenserDetails.reset_cards && (
                <Button
                  variant="contained"
                  onClick={() => openConfirmationModal(ACTION_TYPES.reset)}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Reset Cards
                </Button>
              )}
            </CardActions>
          </>
        )}
      </Card>

      <ConfirmationDialog
        info={modalInfo}
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={handleModalSubmit}
      />

      <DispenserLinkedCardsModel
        open={showLinkedCardsModal}
        onClose={() => setShowLinkedCardsModal(false)}
        dispenserId={dispenserId}
      />

    </Container>
  );
}

export default DispenserDetails;
